/**
 * SIL-1345 — the SINGLE parser from a SKU's `class_attributes` JSONB to the
 * buyer-facing technical-detail tiles (spec 3.2). Both buyer tiers get the
 * same tiles: these are hardware facts, not pricing or evidence.
 *
 * Two rules, both load-bearing:
 *
 *  1. **A missing attribute is OMITTED, never null-filled.** The UI hides a
 *     tile it has no key for; a `null` would make it render an empty tile.
 *     Every field is therefore `?:` and this module never writes a key it
 *     could not resolve to a usable value.
 *
 *  2. **Every lookup checks snake_case AND camelCase.** `class_attributes` is
 *     stored snake_case (db/schema/482/484), but Kysely's CamelCasePlugin
 *     transforms JSONB object keys too, not just column names — so the same
 *     row arrives camelCase through `clientDb` and snake_case through a raw
 *     read or a test fixture. Same discipline as
 *     `cloudVmFieldsFromClassAttributes` (src/sku/naming.ts) and
 *     `sku-matcher.ts`.
 *
 * Fallback keys exist because the catalog is not uniform: cloud_vm rows carry
 * `gpu_count`/`total_gpu_memory_gb`, GPU-part rows carry `gpu_slots`/`vram_gb`.
 * Where a SKU carries neither, the tile is simply absent — that is the designed
 * behaviour, not a gap to paper over with a default.
 */
import type { ListingTechnicalDetails } from './dto/buyer-tier.js';

/**
 * SIL-1381 — moved from `apps/customer/src/components/exchange/copy/listingDetail.ts`
 * (SIL-1348) so the labels sit beside the parser and the type they label. One
 * label per key of `ListingTechnicalDetails`. A key the projection OMITTED
 * gets no tile at all (SIL-1345: absent, not null), so this map is a lookup
 * for keys that are present — never a list to iterate and null-fill.
 *
 * Both the customer portal and the public exchange site (a later ticket)
 * import this constant — one label set, so the two surfaces never drift on
 * what a tile is called for the same SKU.
 */
export const TECHNICAL_DETAIL_LABELS = {
  gpuModel: 'GPU model',
  architecture: 'Architecture',
  gpusPerNode: 'Configuration',
  serverModel: 'Server',
  cpu: 'CPU',
  systemMemoryGb: 'System memory per node',
  networking: 'Networking',
  gpuMemoryGb: 'GPU memory',
} as const;

function asAttrs(classAttributes: unknown): Record<string, unknown> {
  return classAttributes !== null && typeof classAttributes === 'object' && !Array.isArray(classAttributes)
    ? (classAttributes as Record<string, unknown>)
    : {};
}

/** snake_case-or-camelCase lookup for the first key that holds a non-empty string. */
function str(attrs: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = attrs[key] ?? attrs[camel(key)];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return undefined;
}

/** snake_case-or-camelCase lookup for the first key that holds a finite, positive number. */
function positive(attrs: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const value = attrs[key] ?? attrs[camel(key)];
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  }
  return undefined;
}

function camel(snake: string): string {
  return snake.replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase());
}

/**
 * Build the technical-detail tiles for a listing from its SKU's
 * `class_attributes`. Returns `{}` when nothing resolves — the caller sends
 * that through as an empty object and the UI renders no tiles section.
 */
export function technicalDetailsFromClassAttributes(classAttributes: unknown): ListingTechnicalDetails {
  const attrs = asAttrs(classAttributes);
  const out: ListingTechnicalDetails = {};

  const gpuModel = str(attrs, ['gpu_model']);
  if (gpuModel !== undefined) out.gpuModel = gpuModel;

  const architecture = str(attrs, ['architecture', 'gpu_architecture']);
  if (architecture !== undefined) out.architecture = architecture;

  // gpu_count is the cloud_vm/server spelling; gpu_slots is the chassis spelling.
  const gpusPerNode = positive(attrs, ['gpu_count', 'gpu_slots']);
  if (gpusPerNode !== undefined) out.gpusPerNode = gpusPerNode;

  const serverModel = str(attrs, ['server_model', 'model_designation', 'target_systems']);
  if (serverModel !== undefined) out.serverModel = serverModel;

  const cpu = str(attrs, ['cpu_model']);
  if (cpu !== undefined) out.cpu = cpu;

  const systemMemoryGb = positive(attrs, ['system_memory_gb']);
  if (systemMemoryGb !== undefined) out.systemMemoryGb = systemMemoryGb;

  const networking = str(attrs, ['networking', 'network_interface', 'gpu_interconnect', 'host_interface']);
  if (networking !== undefined) out.networking = networking;

  // Total across the node first; a single part's VRAM only as a fallback.
  const gpuMemoryGb = positive(attrs, ['total_gpu_memory_gb', 'gpu_memory_per_gpu_gb', 'vram_gb']);
  if (gpuMemoryGb !== undefined) out.gpuMemoryGb = gpuMemoryGb;

  return out;
}
